import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import ClientAssignFormComponent from '@/components/CommercialPage/ClientAssignForm/ClientAssignFormComponent';
import { useFranchisees } from '@/hooks/useFranchisees';
import { useClients } from '@/hooks/useClients';
import { showToast } from '@/store/ducks/toasts';

const ClientAssignFormContainer = () => {
  const [mailable, setMailable] = useState(false);
  const [selectedFranchisee, setSelectedFranchisee] = useState(null);

  const { id: clientId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const intl = useIntl();

  const { useClientFullDetails } = useClients();
  const { data: client, isLoading: loadingClient } = useClientFullDetails(clientId);
  
  const { useFranchiseesList, useAssignClientToFranchiseeMutation } = useFranchisees();
  const { data: franchiseesData = [], isLoading: loadingFranchisees } = useFranchiseesList();
  
  const assignMutation = useAssignClientToFranchiseeMutation(selectedFranchisee?.value);

  const franchisees = useMemo(() => {
    return franchiseesData.map(f => ({ value: f.id, label: f.name }));
  }, [franchiseesData]);

  const handleChangeFranchisee = useCallback((option) => {
    setSelectedFranchisee(option);
  }, []);

  const handleChangeMailable = useCallback((event) => {
    setMailable(event.target.checked);
  }, []);

  const handleFormSubmit = useCallback((event) => {
    event.preventDefault();

    if (!selectedFranchisee) {
      dispatch(showToast({
        type: 'error',
        title: intl.formatMessage({ id: 'ERROR' }),
        text: intl.formatMessage({ id: 'SELECT_ONE_FRANCHISE_MESSAGE' }),
        duration: 5000,
      }));
      return;
    }

    assignMutation.mutate({ client_id: clientId, mailable }, {
      onSuccess: () => {
        dispatch(showToast({
          type: 'success',
          title: intl.formatMessage({ id: 'SUCCESS' }),
          text: intl.formatMessage({ id: 'CUSTOMER_ASSIGNMENT_SUCCESS' }),
          duration: 5000,
        }));
        navigate('/commercial/clients');
      },
      onError: () => {
        dispatch(showToast({
          type: 'error',
          title: intl.formatMessage({ id: 'ERROR' }),
          text: intl.formatMessage({ id: 'CUSTOMER_ASSIGNMENT_ERROR' }),
          duration: 5000,
        }));
      }
    });
  }, [assignMutation, clientId, dispatch, intl, mailable, navigate, selectedFranchisee]);

  return (
    <ClientAssignFormComponent
      busy={loadingClient || loadingFranchisees}
      requestInProgress={assignMutation.isPending}
      client={client}
      franchisees={franchisees}
      handleChangeFranchisee={handleChangeFranchisee}
      handleChangeMailable={handleChangeMailable}
      onSubmit={handleFormSubmit}
      intl={intl}
    />
  );
};

export default ClientAssignFormContainer;
